import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Marker, OverlayView, Polyline } from '@react-google-maps/api';
import mapboxPolyline from '@mapbox/polyline';

import { Box, IconButton, Map, Tooltip } from '../../ui/core';
import { NavigationPointerIcon } from '../../ui/icons';
import { floatSurfaceSx } from '../../ui/styles';
import { avatarColorFor } from '../../ui/core/IdentityCell';
import { fitPadding, PHONE_MAP_OPTIONS } from '../../components/shared/MapFirstLayout';
import { useDriverGlide } from './driverGlide';
import { mapsReady, SPRITE, spriteStyle, stableBearing } from './carSprite';
import { buildPath, firstIndexAtOrPast, pathAhead, pointAtDistance } from './pathGeometry';
import pickupSvg from '../../assets/map/pickup.svg';
import dropoffSvg from '../../assets/map/dropoff.svg';

// Austin, until the ride's own geometry arrives.
const FALLBACK_CENTER = { lat: 30.2671, lng: -97.74308 };

/**
 * Decodes a route polyline whose precision isn't known up front.
 *
 * A ride's route is always precision 6. A trip's (`{tripId}-active-route`) is
 * whatever its writer used: the server's Google call re-encodes to 6, but the
 * driver app posts its own route at 5 and the api stores it as-is. Decoded at
 * the wrong precision, every point lands 10x toward 0,0 — off West Africa —
 * so this takes whichever precision starts the line nearest `anchor`, a point
 * known to be on the map (a stop, the car). No anchor: 6.
 */
export const decodeRoute = (encoded, anchor) => {
  const at = (precision) => mapboxPolyline.decode(encoded, precision).map(([lat, lng]) => ({ lat, lng }));
  const six = at(6);
  if (!anchor || !six.length) return six;
  const five = at(5);
  const gap = ([first]) => (first.lat - anchor.lat) ** 2 + (first.lng - anchor.lng) ** 2;
  return gap(five) < gap(six) ? five : six;
};

/**
 * Two points a few metres apart would otherwise fit to street level, which is
 * not a useful picture of a car sitting at its pick-up.
 */
const MAX_FIT_ZOOM = 16;

/**
 * What the map should frame in each phase.
 *
 * While the driver is on their way somewhere, the useful picture is the gap
 * they are closing — driver to pick-up, then driver to drop-off — not the
 * whole trip, most of which is either behind them or not their problem yet.
 * Anything else (pre-trip, completed, cancelled) has no journey in progress,
 * so it frames the ride.
 */
export const framedPoints = (phase, { driver, pickup, dropoff, path, stops = [] }) => {
  const pair = {
    driver_en_route_to_pickup: [driver, pickup],
    at_pickup: [driver, pickup],
    en_route_to_dropoff: [driver, dropoff],
    arriving: [driver, dropoff],
  }[phase];

  // Falls back to the whole ride when the pair is not available yet — early in
  // a ride the driver's first fix has not landed.
  const framed = pair?.filter(Boolean);
  return framed?.length === 2 ? framed : [pickup, dropoff, ...stops, ...path, driver].filter(Boolean);
};

/** "now", "4m ago", "2h ago" — how old a rider's last fix is. */
export const ago = (iso, now = Date.now()) => {
  const minutes = Math.floor((now - Date.parse(iso)) / 60000);
  if (!(minutes >= 1)) return 'now';
  return minutes < 60 ? `${minutes}m ago` : `${Math.floor(minutes / 60)}h ago`;
};

/** "AL" for Ana Lopez; "?" when there's no name to go on. */
export const riderInitials = (firstName, lastName) =>
  `${(firstName || '').trim().charAt(0)}${(lastName || '').trim().charAt(0)}`.toUpperCase() || '?';

const RIDER_AVATAR_PX = 32;

/**
 * Where a rider waiting for pick-up last reported being: their photo (their
 * initials on their avatar colour without one, or when it won't load) on the
 * spot, ringed so it can't be mistaken for a place, and how old the fix is
 * beside it. Rider fixes arrive only when they open the app, so the age
 * matters as much as the position.
 *
 * Laid out absolutely from the overlay's 0x0 container rather than centred
 * with a percentage transform — the container has no width to take 50% of.
 */
const RiderMarker = ({ rider }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  const half = RIDER_AVATAR_PX / 2;
  return (
    <OverlayView position={{ lat: rider.lat, lng: rider.lng }} mapPaneName={OverlayView.FLOAT_PANE}>
      {/* Not clickable, yet drawn above the clickable pane: let clicks through
          to whatever is underneath (a driver's dot) rather than swallow them. */}
      <div style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}>
        <div
          title={rider.fullName}
          style={{
            position: 'absolute',
            left: -half,
            top: -half,
            width: RIDER_AVATAR_PX,
            height: RIDER_AVATAR_PX,
            borderRadius: '50%',
            overflow: 'hidden',
            boxSizing: 'border-box',
            border: '2px solid #fff',
            boxShadow: '0 0 0 2px #7C4DFF, 0 2px 6px rgba(0,0,0,0.35)',
            background: avatarColorFor(rider.fullName || rider.key),
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: '600 12px/1 system-ui, sans-serif',
          }}
        >
          {rider.imageUrl && !photoFailed ? (
            <img
              src={rider.imageUrl}
              alt=""
              onError={() => setPhotoFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            rider.initials
          )}
        </div>
        <div
          style={{
            position: 'absolute',
            left: half + 6,
            top: -11,
            whiteSpace: 'nowrap',
            padding: '2px 6px',
            borderRadius: 6,
            background: '#fff',
            color: '#1f1f1f',
            font: '600 11px/16px system-ui, sans-serif',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          }}
        >
          {rider.name} · {ago(rider.timestamp)}
        </div>
      </div>
    </OverlayView>
  );
};

/**
 * One rider as the map draws them. `person` is whatever rider record the page
 * has — `{ firstName, lastName, imageUrl }` — for the face and the name.
 */
export const riderMarker = (location, person) => ({
  key: location.rideId,
  rideId: location.rideId,
  lat: location.lat,
  lng: location.lon,
  timestamp: location.timestamp,
  name: person?.firstName || 'Rider',
  fullName: `${person?.firstName || ''} ${person?.lastName || ''}`.trim(),
  initials: riderInitials(person?.firstName, person?.lastName),
  imageUrl: person?.imageUrl || null,
});

/** A ride stream's `rider` as the `riders` list the map draws, or null. */
export const riderMarkers = (rider, person) => (rider ? [riderMarker(rider, person)] : null);

const point = (waypoint) =>
  waypoint && Number.isFinite(waypoint.lat) && Number.isFinite(waypoint.lon)
    ? { lat: waypoint.lat, lng: waypoint.lon }
    : null;

/**
 * The ride, as its rider's map shows it: pick-up, drop-off, the active leg's
 * route, and the driver where the live stream last put them.
 *
 * Everything comes from the ride-live state, so the map only has what the
 * rider has — if the server is not sending a route, none is drawn here either.
 *
 * `stops` draws a whole trip instead of one ride: a marker per stop (passed
 * ones faded) in place of the ride's pick-up and drop-off, framed on what is
 * still ahead. `state` then carries the trip's route and driver.
 *
 * `riders` marks riders still waiting for pick-up where they last reported
 * being (`{ key, lat, lng, name, timestamp }`); a trip frames them too.
 */
const RideLiveMap = ({ state, sheetPeek, stops = null, riders = null, sprite = SPRITE, children = null }) => {
  const [map, setMap] = useState(null);
  const onLoad = useCallback((instance) => setMap(instance), []);
  // Shown once the viewport is no longer the one we fitted, so the button is an
  // answer to "where did it go" rather than permanent furniture.
  const [pannedAway, setPannedAway] = useState(false);

  const pickup = point(state?.pickup);
  const dropoff = point(state?.dropoff);

  // Memoised on the coordinates themselves. `center` is a CONTROLLED prop: a
  // new object identity re-centres the map, and the glide re-renders this
  // component every frame — which dragged the viewport back to the pickup and
  // made the map impossible to pan.
  const center = useMemo(
    () => (pickup ? { lat: pickup.lat, lng: pickup.lng } : FALLBACK_CENTER),
    // The coordinates, not `pickup` itself: that object is rebuilt every
    // render, which is the identity churn this memo exists to stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pickup?.lat, pickup?.lng],
  );
  // Anchored on what doesn't move — a stop, else the pick-up — so a new fix
  // doesn't re-decode the route; the car only when there is nothing else.
  const firstStop = stops?.find((s) => s.lat !== null && s.lng !== null);
  const anchor =
    (firstStop && { lat: firstStop.lat, lng: firstStop.lng }) || pickup || dropoff || point(state?.driverLocation);
  const anchorKey = anchor ? `${anchor.lat.toFixed(2)},${anchor.lng.toFixed(2)}` : '';
  const path = useMemo(
    () => (state?.routePolyline ? decodeRoute(state.routePolyline, anchor) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state?.routePolyline, anchorKey],
  );

  // The route the car should ride, in metres, prepared once per polyline.
  const route = useMemo(() => buildPath(path), [path]);

  // Tweened along the route, not teleported and not cutting its corners — see
  // useDriverGlide.
  const driver = useDriverGlide(
    state?.driverLocation ? { ...point(state.driverLocation), bearing: state.driverLocation.bearing ?? 0 } : null,
    route,
  );

  // Only the road still to drive, starting exactly under the car. Edited in
  // place rather than re-set: handing Google a new path makes it rebuild and
  // redraw the whole line, which on a long route was the most expensive thing
  // on the page at 60 frames a second. Each frame instead drops the points the
  // car has passed and moves the first point to the car — a couple of edits.
  // The whole path is only set for a new route, a car that moved backwards, or
  // leaving / rejoining the route.
  const [line, setLine] = useState(null);
  const lineState = useRef({ route: null, next: null });
  const along = driver?.along ?? null;
  useEffect(() => {
    if (!line || !mapsReady()) return;
    const drawn = lineState.current;
    if (!route || along === null) {
      const mode = route ? 'full' : 'none';
      if (drawn.route !== route || drawn.next !== mode) {
        line.setPath(route ? route.points : path);
        lineState.current = { route, next: mode };
      }
      return;
    }
    const clamped = Math.max(0, Math.min(route.total, along));
    const next = firstIndexAtOrPast(route, clamped, true);
    if (drawn.route !== route || typeof drawn.next !== 'number' || next < drawn.next) {
      line.setPath(pathAhead(route, clamped));
      lineState.current = { route, next };
      return;
    }
    // Element 0 is the car; element 1 is route.points[drawn.next], and so on.
    const points = line.getPath();
    for (let i = drawn.next; i < next; i += 1) points.removeAt(1);
    const head = pointAtDistance(route, clamped);
    points.setAt(0, new window.google.maps.LatLng(head.lat, head.lng));
    lineState.current = { route, next };
  }, [line, route, path, along]);

  // Held across renders so the frame only changes when the heading has really
  // turned — see stableBearing.
  const drawnBearing = useRef(null);
  drawnBearing.current = driver ? stableBearing(drawnBearing.current, driver.bearing) : null;

  // Framed from the latest fix, not the gliding car: framing only changes when
  // a fix lands, and keying it to the glide rebuilt it (and, before the driver
  // shows, joined the whole route into a string) on every animation frame.
  const phase = state?.phase;
  const fixLat = state?.driverLocation?.lat;
  const fixLon = state?.driverLocation?.lon;
  const framed = useMemo(() => {
    // A trip frames the stops still ahead; once there are none, all of them.
    const ahead = stops?.filter((s) => !s.done);
    const framedStops = (ahead?.length ? ahead : stops || []).filter((s) => s.lat !== null && s.lng !== null);
    // A rider far from their pick-up is the thing to see, so keep them in frame.
    const riderPoints = stops ? (riders || []).map(({ lat, lng }) => ({ lat, lng })) : [];
    return framedPoints(phase, {
      driver: point(state?.driverLocation),
      pickup,
      dropoff,
      path,
      stops: [...framedStops, ...riderPoints],
    });
    // The coordinates, not the point objects: those are rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, fixLat, fixLon, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, path, stops, riders]);
  const framedKey = useMemo(() => framed.map((p) => `${p.lat},${p.lng}`).join('|'), [framed]);

  const fitToRide = useCallback(() => {
    if (!map || !mapsReady() || !framed.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    framed.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
    map.fitBounds(bounds, fitPadding(map, sheetPeek));
    // A car at its pick-up is two points almost on top of each other, which
    // fits to a rooftop.
    if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
    setPannedAway(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, sheetPeek, framedKey]);

  // Re-frame when what should be framed changes — a new ride, or a phase that
  // swaps pick-up for drop-off. Not on every ping: the driver moves constantly
  // and a camera that chased them would fight whoever is reading the map.
  const rideId = state?.rideId ?? state?.tripId;
  useEffect(() => {
    fitToRide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, rideId, phase, path.length]);

  // Then follow, but only as much as it takes: while the viewport is still
  // ours, a fix that leaves something framed off-screen re-frames. A driver
  // crossing the middle of the map moves nothing.
  useEffect(() => {
    if (!map || pannedAway || !mapsReady() || !framed.length) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const allVisible = framed.every((p) => bounds.contains(new window.google.maps.LatLng(p.lat, p.lng)));
    if (!allVisible) fitToRide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixLat, fixLon]);

  // Only a human dragging or zooming counts as panning away — `fitBounds` moves
  // the camera too, and reacting to that would show the button immediately.
  useEffect(() => {
    if (!map || !mapsReady()) return undefined;
    const listeners = [
      map.addListener('dragstart', () => setPannedAway(true)),
      map.addListener('click', () => undefined),
    ];
    const wheel = () => setPannedAway(true);
    const container = map.getDiv();
    container.addEventListener('wheel', wheel, { passive: true });
    return () => {
      listeners.forEach((listener) => listener.remove());
      container.removeEventListener('wheel', wheel);
    };
  }, [map]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        center={center}
        zoom={11}
        onLoad={onLoad}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        // The map is the page, so a finger on it moves it — not the page.
        options={{ gestureHandling: 'greedy', ...(sheetPeek && PHONE_MAP_OPTIONS) }}
      >
        {path.length > 1 && (
          <Polyline
            onLoad={(instance) => {
              lineState.current = { route: null, next: null };
              setLine(instance);
            }}
            onUnmount={() => setLine(null)}
            options={{ strokeColor: '#000000', strokeWeight: 4, strokeOpacity: 0.9 }}
          />
        )}
        {pickup && <Marker position={pickup} icon={pickupSvg} clickable={false} />}
        {dropoff && <Marker position={dropoff} icon={dropoffSvg} clickable={false} />}
        {stops
          ?.filter((s) => s.lat !== null && s.lng !== null)
          .map((s) => (
            <Marker
              key={s.index}
              position={{ lat: s.lat, lng: s.lng }}
              icon={s.steps[0]?.type === 'pick-up' ? pickupSvg : dropoffSvg}
              opacity={s.done ? 0.4 : 1}
              // Pins don't take clicks; a driver dot beneath one still can.
              clickable={false}
            />
          ))}
        {/* An overlay, not a Marker: Marker snaps to whole pixels, and at a zoom
            that frames the whole trip one ping moves the car less than a pixel —
            so it sat still and then hopped. An overlay is positioned from
            `fromLatLngToDivPixel`, which is fractional. */}
        {driver && (
          <OverlayView
            position={{ lat: driver.lat, lng: driver.lng }}
            // `overlayLayer` is where the polyline draws, so the car came out
            // underneath the route. `floatPane` is above both.
            mapPaneName={OverlayView.FLOAT_PANE}
          >
            {/* Centred with a transform rather than `getPixelPositionOffset`:
                that callback measures the wrapper, which has no layout size yet
                when it runs, so the offset came back 0 and the car hung below
                and to the right of its own position. */}
            <div
              style={{
                ...spriteStyle(drawnBearing.current, sprite),
                // translate3d + will-change puts the car on its own compositor
                // layer. Without it the browser re-rasterises a bitmap sitting at
                // fractional pixels every frame, which shows up as the car
                // shivering sideways a fraction of a pixel.
                transform: 'translate3d(-50%, -50%, 0)',
                willChange: 'transform',
                // Drawn above the clickable pane but not clickable itself.
                pointerEvents: 'none',
              }}
            />
          </OverlayView>
        )}
        {/* After the car: same pane, later in the DOM, so a rider's label reads
            on top of a car parked at their pick-up. */}
        {riders?.map((rider) => (
          <RiderMarker key={rider.key} rider={rider} />
        ))}
        {/* Whatever else the caller draws on this map (e.g. drivers nearby). */}
        {children}
      </Map>

      {/* Bottom-trailing, and only once the viewport is no longer the one we
          fitted — an answer to "where did it go", not permanent furniture. */}
      {pannedAway && (
        <Tooltip title="Recenter on the ride">
          <IconButton
            onClick={fitToRide}
            sx={{
              position: 'absolute',
              right: 16,
              bottom: sheetPeek ? `calc(${sheetPeek * 100}% + 16px)` : 24,
              backgroundColor: (theme) => theme.palette.background.paper,
              '&:hover': { backgroundColor: (theme) => theme.palette.background.paper },
              ...floatSurfaceSx,
            }}
          >
            <NavigationPointerIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default RideLiveMap;
